import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Check } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const TYPE_COLORS = {
  success: 'bg-[#28cd8c]',
  warning: 'bg-[#f6a609]',
  error: 'bg-[#ea4b5a]',
  info: 'bg-[#7d64ff]',
};

const TYPE_TOAST = {
  success: toast.success,
  warning: toast.warning,
  error: toast.error,
  info: toast.info,
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const ref = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications`, { withCredentials: true });
      setNotifications(res.data.notifications || []);
      setUnread(res.data.unread_count || 0);
    } catch {}
  }, []);

  // WebSocket connection
  const connectWs = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState <= 1) return;

    // Get token from cookies
    const cookies = document.cookie.split(';').reduce((acc, c) => {
      const [k, v] = c.trim().split('=');
      acc[k] = v;
      return acc;
    }, {});
    const token = cookies['access_token'] || '';
    if (!token) return;

    try {
      const ws = new WebSocket(`${WS_URL}/api/ws/notifications?token=${token}`);

      ws.onopen = () => {
        setWsConnected(true);
        // Heartbeat
        const ping = setInterval(() => {
          if (ws.readyState === 1) ws.send('ping');
          else clearInterval(ping);
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'notification' && data.data) {
            const notif = data.data;
            setNotifications(prev => [notif, ...prev].slice(0, 30));
            setUnread(prev => prev + 1);
            // Show toast
            const toastFn = TYPE_TOAST[notif.type] || toast.info;
            toastFn(notif.title, { description: notif.message });
          }
        } catch {}
      };

      ws.onclose = () => {
        setWsConnected(false);
        wsRef.current = null;
        // Reconnect after 5s
        reconnectTimer.current = setTimeout(connectWs, 5000);
      };

      ws.onerror = () => {
        ws.close();
      };

      wsRef.current = ws;
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    connectWs();

    // Fallback polling every 30s if WS is down
    const interval = setInterval(() => {
      if (!wsRef.current || wsRef.current.readyState !== 1) {
        fetchNotifications();
        connectWs();
      }
    }, 30000);

    return () => {
      clearInterval(interval);
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [fetchNotifications, connectWs]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markAllRead = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/mark-read`, {}, { withCredentials: true });
      setUnread(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-[#5a5c87] hover:text-[#0A0A0A] hover:bg-[#f8f7ff] rounded-lg transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#ea4b5a] text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
        {/* WS status dot */}
        <span className={`absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full ${wsConnected ? 'bg-[#28cd8c]' : 'bg-[#e5e5ea]'}`} title={wsConnected ? 'Real-time connected' : 'Polling mode'} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-[#e5e5ea] rounded-xl shadow-xl z-50" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#e5e5ea]">
            <div className="flex items-center gap-2">
              <h4 className="font-bold text-sm">Notifications</h4>
              {wsConnected && <span className="w-2 h-2 rounded-full bg-[#28cd8c]" title="Live" />}
            </div>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-xs text-[#7d64ff] hover:text-[#6153a7] font-medium flex items-center gap-1" data-testid="mark-all-read">
                <Check size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#5a5c87]">No notifications</div>
            ) : (
              notifications.slice(0, 15).map(n => (
                <div key={n.notification_id} className={`px-4 py-3 border-b border-[#f0f0f0] hover:bg-[#f8f7ff] transition-colors ${!n.read ? 'bg-[#f8f7ff]' : ''}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-2 h-2 mt-1.5 rounded-full flex-shrink-0 ${TYPE_COLORS[n.type] || TYPE_COLORS.info}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0A0A0A] truncate">{n.title}</p>
                      <p className="text-xs text-[#5a5c87] mt-0.5">{n.message}</p>
                      <p className="text-[10px] text-[#7c7fa0] mt-1">{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
